/**
 * The builder's single source of truth (Doc 04 §5.2).
 *
 * History is stored as Immer patches, not snapshots. A draft with several languages and
 * a rule set is easily 100-300 kB of JSON; a hundred snapshots would be tens of
 * megabytes, while a patch pair is typically under a kilobyte. The patches are also
 * exactly what a later collaborative mode would need.
 *
 * text_hash is never computed here. It is the evidence anchor and is produced by the
 * backend at publish time from the finished bundle — a client-side copy would be a
 * second implementation of the one value that has to be authoritative.
 */
import { applyPatches, enablePatches, produceWithPatches, type Patch } from 'immer';
import { create, type StoreApi, type UseBoundStore } from 'zustand';

import type { Draft, DraftCategory } from '../../../lib/types';

enablePatches();

export type SaveState = 'idle' | 'saving' | 'saved' | 'error' | 'conflict';

interface HistoryEntry {
  label: string;
  do_: Patch[];
  undo: Patch[];
  at: number;
  mergeKey?: string;
}

export interface Command {
  label: string;
  recipe: (d: Draft) => void;
  /** Bursts sharing a key inside the merge window collapse into one undo step. */
  merge?: string;
}

export interface DraftState {
  draft: Draft;
  /** The last server-confirmed state, for the diff and for conflict resolution. */
  saved: Draft;
  etag: string | null;
  past: HistoryEntry[];
  future: HistoryEntry[];
  dirty: boolean;
  saveState: SaveState;
  lastSavedAt: number | null;

  apply: (cmd: Command) => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  markSaving: () => void;
  markSaved: (server: Draft, etag: string | null) => void;
  markFailed: (conflict: boolean) => void;
  /** Discard local edits and take the server's state (conflict dialog). */
  takeServer: (server: Draft, etag: string | null) => void;
}

const MAX_HISTORY = 100;
// Long enough to swallow a typing burst or a slider drag, short enough that two
// deliberate edits stay separately undoable.
const MERGE_WINDOW_MS = 600;

export type DraftStore = UseBoundStore<StoreApi<DraftState>>;

export function createDraftStore(initial: Draft, etag: string | null): DraftStore {
  return create<DraftState>()((set, get) => ({
    draft: initial,
    saved: initial,
    etag,
    past: [],
    future: [],
    dirty: false,
    saveState: 'idle',
    lastSavedAt: null,

    apply: ({ label, recipe, merge }) => {
      const s = get();
      const [next, do_, undo] = produceWithPatches(s.draft, recipe);
      // A no-op must not consume an undo step, or Ctrl+Z starts doing nothing visible.
      if (do_.length === 0) return;

      const last = s.past.at(-1);
      const canMerge = !!merge && last?.mergeKey === merge && Date.now() - last.at < MERGE_WINDOW_MS;

      const past = canMerge
        ? [
            ...s.past.slice(0, -1),
            // Undo patches accumulate in reverse: the oldest state must be restored last.
            { ...last!, do_: [...last!.do_, ...do_], undo: [...undo, ...last!.undo], at: Date.now() },
          ]
        : [...s.past, { label, do_, undo, at: Date.now(), mergeKey: merge }].slice(-MAX_HISTORY);

      set({ draft: next, past, future: [], dirty: true });
    },

    undo: () =>
      set((s) => {
        const e = s.past.at(-1);
        if (!e) return s;
        return {
          draft: applyPatches(s.draft, e.undo),
          past: s.past.slice(0, -1),
          future: [e, ...s.future],
          dirty: true,
        };
      }),

    redo: () =>
      set((s) => {
        const [e, ...rest] = s.future;
        if (!e) return s;
        return {
          draft: applyPatches(s.draft, e.do_),
          past: [...s.past, e],
          future: rest,
          dirty: true,
        };
      }),

    canUndo: () => get().past.length > 0,
    canRedo: () => get().future.length > 0,

    markSaving: () => set({ saveState: 'saving' }),
    markSaved: (server, newEtag) =>
      set((s) => ({
        saved: server,
        etag: newEtag,
        saveState: 'saved',
        lastSavedAt: Date.now(),
        // Only clean if nothing changed while the request was in flight.
        dirty: JSON.stringify(s.draft) !== JSON.stringify(server),
      })),
    markFailed: (conflict) => set({ saveState: conflict ? 'conflict' : 'error' }),
    takeServer: (server, newEtag) =>
      set({
        draft: server,
        saved: server,
        etag: newEtag,
        past: [],
        future: [],
        dirty: false,
        saveState: 'saved',
      }),
  }));
}

// ── Recipes ─────────────────────────────────────────────────────────────────────────
// Named commands rather than a generic setter, so the undo list reads as a list of
// things the user did rather than a list of paths that changed.

type UI = Record<string, any>;
const ui = (d: Draft): UI => (d.ui ??= {}) as UI;

export const setText = (lang: string, field: 'title' | 'body', value: string): Command => ({
  label: field === 'title' ? 'Überschrift geändert' : 'Text geändert',
  merge: `texts.${lang}.${field}`,
  recipe: (d) => {
    d.texts ??= {};
    d.texts[lang] = { ...(d.texts[lang] ?? {}), [field]: value };
  },
});

export const setLayout = (layout: string): Command => ({
  label: 'Layout geändert',
  recipe: (d) => {
    ui(d)['layout'] = layout;
  },
});

export const setTheme = (key: string, value: string | number | boolean): Command => ({
  label: `${key} geändert`,
  merge: `theme.${key}`,
  recipe: (d) => {
    const u = ui(d);
    u['theme'] = { ...(u['theme'] ?? {}), [key]: value };
  },
});

/**
 * One half of a house font: `ui.fonts.headline` or `ui.fonts.body`.
 *
 * Family and URL are written separately because they are typed separately, and a face is
 * only loaded once both are there — see validate.go, which refuses half of one rather
 * than letting it fail silently in the browser.
 */
export const setFont = (role: 'headline' | 'body', patch: Record<string, unknown>): Command => ({
  label: 'Schrift geändert',
  merge: `fonts.${role}.${Object.keys(patch).join(',')}`,
  recipe: (d) => {
    const u = ui(d);
    const fonts = (u['fonts'] ??= {}) as UI;
    fonts[role] = { ...(fonts[role] ?? {}), ...patch };
  },
});

export const setButton = (key: string, patch: Record<string, unknown>): Command => ({
  label: 'Button geändert',
  merge: `buttons.${key}.${Object.keys(patch).join(',')}`,
  recipe: (d) => {
    const u = ui(d);
    const buttons = (u['buttons'] ??= {}) as UI;
    buttons[key] = { ...(buttons[key] ?? {}), ...patch };
  },
});

/**
 * The order of the banner's action buttons.
 *
 * Stored whole rather than as a move-one-step patch, because undo has to restore an
 * arrangement, not replay a sequence of swaps.
 */
export const setButtonOrder = (order: string[]): Command => ({
  label: 'Button-Reihenfolge geändert',
  recipe: (d) => {
    const u = ui(d);
    u['buttons'] = { ...(u['buttons'] ?? {}), order };
  },
});

/** The same, one layer down: the order of the preferences dialog's action buttons. */
export const setPrefsButtonOrder = (order: string[]): Command => ({
  label: 'Button-Reihenfolge im Auswahl-Dialog geändert',
  recipe: (d) => {
    const u = ui(d);
    u['buttons'] = { ...(u['buttons'] ?? {}), prefs_order: order };
  },
});

export const setEqualWeight = (on: boolean): Command => ({
  label: on ? 'Gleichgewichtung an' : 'Gleichgewichtung aus',
  recipe: (d) => {
    const u = ui(d);
    u['buttons'] = { ...(u['buttons'] ?? {}), equal_weight: on };
  },
});

export const setReopen = (patch: Record<string, unknown>): Command => ({
  label: 'Badge geändert',
  recipe: (d) => {
    const u = ui(d);
    u['reopen'] = { ...(u['reopen'] ?? {}), ...patch };
  },
});

export const reorderCategories = (from: number, to: number): Command => ({
  label: 'Kategorien umsortiert',
  recipe: (d) => {
    const list = d.categories;
    const [moved] = list.splice(from, 1);
    if (moved) list.splice(to, 0, moved);
    // sort_order is what the bundle builder orders by, so it has to follow the list.
    list.forEach((c, i) => {
      c.sort_order = (i + 1) * 10;
    });
  },
});

export const setCategory = (slug: string, patch: Partial<DraftCategory>): Command => ({
  label: 'Kategorie geändert',
  recipe: (d) => {
    const c = d.categories.find((x) => x.slug === slug);
    if (c) Object.assign(c, patch);
  },
});

export const setCategoryLabel = (
  slug: string,
  lang: string,
  field: 'label' | 'desc',
  value: string,
): Command => ({
  label: 'Kategorietext geändert',
  merge: `cat.${slug}.${lang}.${field}`,
  recipe: (d) => {
    const c = d.categories.find((x) => x.slug === slug);
    if (!c) return;
    c.labels ??= {};
    c.labels[lang] = { ...(c.labels[lang] ?? {}), [field]: value };
  },
});

export const addCategory = (slug: string, lang: string, label: string): Command => ({
  label: 'Kategorie hinzugefügt',
  recipe: (d) => {
    if (d.categories.some((c) => c.slug === slug)) return;
    d.categories.push({
      slug,
      required: false,
      default: false,
      sort_order: (d.categories.length + 1) * 10,
      labels: { [lang]: { label, desc: '' } },
    });
  },
});

export const removeCategory = (slug: string): Command => ({
  label: 'Kategorie entfernt',
  recipe: (d) => {
    d.categories = d.categories.filter((c) => c.slug !== slug);
  },
});

/** Generic setter for the additive ui.* groups (logo, close, behaviour, box, prefs). */
export const setGroup = (group: string, patch: Record<string, unknown>, label: string): Command => ({
  label,
  merge: `${group}.${Object.keys(patch).join(',')}`,
  recipe: (d) => {
    const u = ui(d);
    u[group] = { ...(u[group] ?? {}), ...patch };
  },
});

/**
 * Links land in ui.texts.links in the finished bundle, but the editor keeps them under
 * draft.texts[lang] with the rest of the copy — an Impressum URL is not the same in
 * every locale, and the builder cuts texts per language anyway.
 */
export const setLinks = (lang: string, links: { label: string; url: string }[]): Command => ({
  label: 'Links geändert',
  recipe: (d) => {
    d.texts ??= {};
    d.texts[lang] = { ...(d.texts[lang] ?? {}), links };
  },
});

export const setPrefsText = (
  lang: string,
  field: 'prefs_title' | 'prefs_intro' | 'always_active' | 'close_label',
  value: string,
): Command => ({
  label: 'Text der Auswahl geändert',
  merge: `texts.${lang}.${field}`,
  recipe: (d) => {
    d.texts ??= {};
    d.texts[lang] = { ...(d.texts[lang] ?? {}), [field]: value };
  },
});

export const setLanguages = (langs: string[]): Command => ({
  label: 'Sprachen geändert',
  recipe: (d) => {
    d.i18n_available = langs;
  },
});
